// src/api/moveHistoryService.ts (UPDATED - WITH INITIAL BOARD STATE)
import axios from "axios";
import { Move } from "../utils/MoveHistoryManager.ts";

const API_URL = process.env.REACT_APP_API_URL || "";

/**
 * Helper function to get the authentication token
 */
const getAuthToken = (): string | null => {
  const token = localStorage.getItem("token");
  if (token) return token;

  const sessionToken = sessionStorage.getItem("token");
  if (sessionToken) return sessionToken;

  return null;
};

/**
 * Helper function to get auth headers
 */
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

/**
 * Enhanced interface to include BOTH initial and final game state
 */
export interface SavedMoveHistory {
  id: number;
  user_id: number;
  name: string;
  game_preset: string;
  board_size: number;
  moves: Move[];
  total_moves: number;

  // ⭐ NEW: Initial board state (for navigation replay)
  initial_pieces?: Array<[string, any]>;

  // Final board state (current position)
  pieces?: Array<[string, any]>;
  current_turn?: "white" | "black";
  game_status?: "active" | "check" | "checkmate" | "stalemate";
  captured_pieces?: any[];
  halfmove_clock?: number;

  created_at: string;
  updated_at?: string;
}

/**
 * Save move history with BOTH initial and final game state
 * ⭐ KEY CHANGE: Now includes initial_pieces for navigation
 */
export const saveMoveHistory = async (
  name: string,
  gamePreset: string,
  boardSize: number,
  moves: Move[],
  initialPieces: Map<string, any>, // ⭐ NEW PARAMETER
  pieces: Map<string, any>,
  currentTurn: "white" | "black",
  gameStatus: "active" | "check" | "checkmate" | "stalemate",
  capturedPieces: any[],
  halfmoveClock: number
): Promise<SavedMoveHistory> => {
  console.log("=== saveMoveHistory API call (WITH INITIAL STATE) ===");
  console.log("Name:", name);
  console.log("Game preset:", gamePreset);
  console.log("Board size:", boardSize);
  console.log("Total moves:", moves.length);
  console.log("Initial pieces count:", initialPieces.size);
  console.log("Final pieces count:", pieces.size);
  console.log("Current turn:", currentTurn);
  console.log("Game status:", gameStatus);

  try {
    // Convert both initial and final pieces Maps to arrays
    const initialPiecesArray = Array.from(initialPieces.entries());
    const finalPiecesArray = Array.from(pieces.entries());

    console.log("Initial pieces array length:", initialPiecesArray.length);
    console.log("Final pieces array length:", finalPiecesArray.length);

    const payload = {
      name: name.trim(),
      game_preset: gamePreset,
      board_size: boardSize,
      moves: moves,
      total_moves: moves.length,
      initial_pieces: initialPiecesArray, // ⭐ NEW FIELD
      pieces: finalPiecesArray,
      current_turn: currentTurn,
      game_status: gameStatus,
      captured_pieces: capturedPieces,
      halfmove_clock: halfmoveClock,
    };

    console.log("Sending payload with initial state:", {
      ...payload,
      moves: `${payload.moves.length} moves`,
      initial_pieces: `${payload.initial_pieces.length} pieces`,
      pieces: `${payload.pieces.length} pieces`,
    });

    const response = await axios.post(`${API_URL}/api/move-history`, payload, {
      headers: getAuthHeaders(),
      withCredentials: true,
    });

    console.log("✅ Save successful! Response:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("=== saveMoveHistory API error ===");
    console.error("Error:", error);
    console.error("Response data:", error.response?.data);
    console.error("Response status:", error.response?.status);
    throw error;
  }
};

/**
 * Get all saved move histories for the current user
 */
export const getMoveHistories = async (): Promise<SavedMoveHistory[]> => {
  console.log("=== getMoveHistories API call ===");

  try {
    const response = await axios.get(`${API_URL}/api/move-history`, {
      headers: getAuthHeaders(),
      withCredentials: true,
    });

    const histories = response.data.histories || [];
    console.log("✅ Loaded", histories.length, "histories");
    return histories;
  } catch (error: any) {
    console.error("=== getMoveHistories API error ===");
    console.error("Error:", error);
    console.error("Response data:", error.response?.data);
    throw error;
  }
};

/**
 * Get a specific move history by ID
 */
export const getMoveHistory = async (id: number): Promise<SavedMoveHistory> => {
  console.log("=== getMoveHistory API call ===", id);

  try {
    const response = await axios.get(`${API_URL}/api/move-history/${id}`, {
      headers: getAuthHeaders(),
      withCredentials: true,
    });

    const history = response.data.history;
    console.log("✅ Loaded history:", history.name);
    return history;
  } catch (error: any) {
    console.error("=== getMoveHistory API error ===");
    console.error("Error:", error);
    throw error;
  }
};

/**
 * Delete a saved move history
 */
export const deleteMoveHistory = async (historyId: number): Promise<void> => {
  console.log("=== deleteMoveHistory API call ===", historyId);

  try {
    await axios.delete(`${API_URL}/api/move-history/${historyId}`, {
      headers: getAuthHeaders(),
      withCredentials: true,
    });

    console.log("✅ Delete successful!");
  } catch (error: any) {
    console.error("=== deleteMoveHistory API error ===");
    console.error("Error:", error);
    throw error;
  }
};

/**
 * Export moves to a JSON file (client-side only, no API call)
 */
export const exportMovesToFile = (
  moveHistory: Move[],
  gamePreset: string
): void => {
  console.log("=== exportMovesToFile ===");
  console.log("Exporting", moveHistory.length, "moves");

  const data = {
    gamePreset,
    exportDate: new Date().toISOString(),
    totalMoves: moveHistory.length,
    moves: moveHistory,
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `chess-moves-${gamePreset}-${Date.now()}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  console.log("✅ Export complete!");
};
